/**
 * Per-client clock offset estimation from ping/pong samples.
 *
 * offset = serverTime - clientTime. Each pong round-trip yields one sample:
 * offset ≈ s - (c0 + rtt/2). We keep a sliding window and trust the sample
 * with the smallest RTT (least queueing delay → most accurate), which is the
 * standard NTP-ish trick and is robust against asymmetric jitter spikes.
 */
export interface ClockSample { rtt: number; offset: number; at: number }

export class ClockEstimator {
  private samples: ClockSample[] = []
  constructor(private readonly windowSize = 16) {}

  /** @param c0 client send time, @param s server receive time, @param c1 client receive time */
  addSample(c0: number, s: number, c1: number): void {
    const rtt = Math.max(0, c1 - c0)
    const offset = s - (c0 + rtt / 2)
    this.samples.push({ rtt, offset, at: c1 })
    if (this.samples.length > this.windowSize) this.samples.shift()
  }

  get ready(): boolean { return this.samples.length > 0 }

  /** Best (min-RTT) offset, or 0 before any sample. */
  get offset(): number {
    if (!this.samples.length) return 0
    let best = this.samples[0]
    for (const s of this.samples) if (s.rtt < best.rtt) best = s
    return best.offset
  }

  /** Smoothed RTT (median of window). */
  get rtt(): number {
    if (!this.samples.length) return 0
    const sorted = this.samples.map((s) => s.rtt).sort((a, b) => a - b)
    return sorted[Math.floor(sorted.length / 2)]
  }

  get minRtt(): number {
    return this.samples.reduce((m, s) => Math.min(m, s.rtt), Infinity)
  }

  toServerTime(clientTime: number): number { return clientTime + this.offset }
}

